import { useMemo, useState } from "react";
import type { Identifier } from "ra-core";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Focus,
  ScanSearch,
  ShieldCheck,
  Wrench,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type {
  EchoflicksFramingQcSummary,
  EchoflicksFramingSubjectSummary,
  EchoflicksSessionFeedback,
  EchoflicksSessionJourney,
  EchoflicksSessionMedia,
} from "../types";
import { FramingFeedbackEvidence } from "../feedback/FramingFeedbackEvidence";
import { feedbackRecordToSessionFeedback } from "../feedback/framingFeedback";
import { useEchoFeedbackReports } from "../feedback/useEchoFeedbackReports";
import { EmptyHint } from "./EchoflicksSessionList";
import { FramingEvidenceImage } from "./FramingEvidenceImage";
import { FramingSceneInspectorPanel } from "./FramingSceneInspectorPanel";
import {
  selectCorrelatedFramingDiagnostic,
  selectLatestFramingReport,
} from "./framingMedia";
import { formatDateTime, formatMs } from "./sessionUtils";
import { useMediaUrl } from "./useMediaUrl";
import {
  FILTER_LABELS,
  buildFramingScenes,
  filterFramingScenes,
  hasCropRelatedFeedback,
  isCropRelatedFeedback,
  type FramingSceneFilter,
} from "./framingDiagnostics";

function subjectLabel(subject: EchoflicksFramingSubjectSummary): string {
  const name = subject.label ?? subject.subject_id ?? subject.kind ?? "subject";
  const kind =
    subject.kind && subject.kind !== name ? ` · ${subject.kind}` : "";
  return `${name}${kind}`;
}

function issueLabel(issue: string): string {
  return issue.replaceAll("_", " ");
}

function metricText(metrics?: EchoflicksFramingQcSummary["metrics"]): string {
  if (!metrics) return "";
  return Object.entries(metrics)
    .slice(0, 5)
    .map(([key, value]) => `${issueLabel(key)} ${String(value)}`)
    .join(" · ");
}

function statusIcon(status?: string | null) {
  if (status === "failed")
    return <XCircle className="size-4 text-destructive" />;
  if (status === "passed" || status === "repaired") {
    return <CheckCircle2 className="size-4 text-emerald-600" />;
  }
  return <AlertTriangle className="size-4 text-amber-600" />;
}

export function FramingDiagnosticsCard({
  journey,
  media,
  feedback,
  isLoading,
  sessionId,
  canViewTechnical,
}: {
  journey?: EchoflicksSessionJourney;
  media: EchoflicksSessionMedia[];
  feedback: EchoflicksSessionFeedback[];
  isLoading?: boolean;
  sessionId: Identifier;
  canViewTechnical: boolean;
}) {
  const [filter, setFilter] = useState<FramingSceneFilter>("all");
  const reviewerFeedbackQuery = useEchoFeedbackReports(String(sessionId));
  const reviewerReports = useMemo(
    () => reviewerFeedbackQuery.data?.data ?? [],
    [reviewerFeedbackQuery.data?.data],
  );
  const cropReviewerReports = useMemo(
    () =>
      reviewerReports.filter(
        (report) =>
          report.echo.id === String(sessionId) &&
          report.categories.some((category) => category.id === "framing_crop"),
      ),
    [reviewerReports, sessionId],
  );
  const combinedFeedback = useMemo(
    () => [
      ...feedback,
      ...reviewerReports.map((report) =>
        feedbackRecordToSessionFeedback(report, journey?.framing),
      ),
    ],
    [feedback, journey?.framing, reviewerReports],
  );
  const scenes = useMemo(
    () => buildFramingScenes(journey?.framing),
    [journey?.framing],
  );
  const cropFeedback = useMemo(
    () => hasCropRelatedFeedback(combinedFeedback),
    [combinedFeedback],
  );
  const cropFeedbackSceneIds = useMemo(
    () =>
      new Set(
        combinedFeedback
          .filter(
            (entry) => entry.scene_id != null && isCropRelatedFeedback(entry),
          )
          .map((entry) => String(entry.scene_id)),
      ),
    [combinedFeedback],
  );
  const filteredScenes = useMemo(
    () =>
      filterFramingScenes(scenes, filter, cropFeedback, cropFeedbackSceneIds),
    [cropFeedback, cropFeedbackSceneIds, filter, scenes],
  );
  const summary = journey?.summary?.framing;
  const reportMedia = useMemo(() => selectLatestFramingReport(media), [media]);
  const reportUrl = useMediaUrl(
    canViewTechnical
      ? summary?.report_storage_path
        ? {
            storage_bucket: summary.report_storage_bucket,
            storage_path: summary.report_storage_path,
            storage_is_public: false,
          }
        : reportMedia
      : null,
  );

  if (!canViewTechnical) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Focus className="size-4" />
              Subject-aware framing
            </CardTitle>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Shows the pipeline's framing records. Open a coordinate inspector
              to distinguish planned geometry from worker-applied and encoded
              delivery evidence.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {summary?.status ? (
              <Badge
                variant={summary.at_risk ? "destructive" : "secondary"}
                className="capitalize"
              >
                {summary.status.replaceAll("_", " ")}
              </Badge>
            ) : null}
            {reportUrl.url ? (
              <Button asChild variant="outline" size="sm">
                <a href={reportUrl.url} target="_blank" rel="noreferrer">
                  Framing report
                  <ExternalLink className="size-3.5" />
                </a>
              </Button>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <FramingFeedbackEvidence
          reports={cropReviewerReports}
          framingEvents={journey?.framing}
        />
        {isLoading ? (
          <EmptyHint>Loading framing telemetry…</EmptyHint>
        ) : scenes.length === 0 ? (
          <EmptyHint>
            No subject-aware framing telemetry exists for this echo. Legacy and
            flag-off renders continue to use the established crop pipeline.
          </EmptyHint>
        ) : (
          <>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
              <div className="rounded-md border p-3 text-sm">
                <div className="text-muted-foreground">Scenes observed</div>
                <div className="mt-1 font-mono text-lg">{scenes.length}</div>
              </div>
              <div className="rounded-md border p-3 text-sm">
                <div className="text-muted-foreground">Failed QC</div>
                <div className="mt-1 font-mono text-lg">
                  {summary?.failed_scene_count ??
                    scenes.filter((scene) => scene.qc?.status === "failed")
                      .length}
                </div>
              </div>
              <div className="rounded-md border p-3 text-sm">
                <div className="text-muted-foreground">Repaired</div>
                <div className="mt-1 font-mono text-lg">
                  {summary?.repaired_scene_count ??
                    scenes.filter((scene) => scene.repaired).length}
                </div>
              </div>
              <div className="rounded-md border p-3 text-sm">
                <div className="text-muted-foreground">Fallbacks</div>
                <div className="mt-1 font-mono text-lg">
                  {summary?.fallback_scene_count ??
                    scenes.filter((scene) => scene.fallback).length}
                </div>
              </div>
              <div className="rounded-md border p-3 text-sm">
                <div className="text-muted-foreground">Tracking crops</div>
                <div className="mt-1 font-mono text-lg">
                  {summary?.tracking_scene_count ??
                    scenes.filter((scene) => scene.strategy === "tracking_crop")
                      .length}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ShieldCheck className="size-4" />
                Diagnostics omit identity embeddings and raw model responses.
              </div>
              <Select
                value={filter}
                onValueChange={(value) =>
                  setFilter(value as FramingSceneFilter)
                }
              >
                <SelectTrigger
                  size="sm"
                  className="min-w-52"
                  aria-label="Filter framing scenes"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(
                    Object.entries(FILTER_LABELS) as Array<
                      [FramingSceneFilter, string]
                    >
                  ).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {filteredScenes.length === 0 ? (
              <EmptyHint>
                {filter === "crop_feedback" && cropFeedback
                  ? "Crop feedback exists but no scene could be correlated. " +
                    "The reports stay in the uncorrelated bucket; no scene is " +
                    "claimed as a match."
                  : "No scenes match this framing filter."}
              </EmptyHint>
            ) : (
              <div className="space-y-3">
                {filteredScenes.map((scene) => (
                  <article
                    key={scene.sceneId}
                    id={`framing-scene-${scene.sceneId}`}
                    className="rounded-md border p-4"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex items-start gap-2">
                        {statusIcon(scene.qc?.status)}
                        <div>
                          <h3 className="font-medium">Scene {scene.sceneId}</h3>
                          <p className="text-xs text-muted-foreground">
                            {scene.planId
                              ? `Plan ${scene.planId}${scene.planRevision != null ? ` · revision ${scene.planRevision}` : ""}`
                              : "Plan id unavailable"}
                          </p>
                          <p className="font-mono text-[10px] text-muted-foreground">
                            attempt {scene.attemptId ?? "legacy"}
                            {scene.attemptCount > 1
                              ? ` · ${scene.attemptCount} attempts in history`
                              : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {scene.strategy ? (
                          <Badge variant="outline">
                            <ScanSearch className="size-3" />
                            {issueLabel(scene.strategy)}
                          </Badge>
                        ) : null}
                        {scene.repaired ? (
                          <Badge variant="secondary">
                            <Wrench className="size-3" /> repaired
                          </Badge>
                        ) : null}
                        {scene.fallback ? (
                          <Badge variant="secondary">fallback</Badge>
                        ) : null}
                        {cropFeedbackSceneIds.has(scene.sceneId) ? (
                          <Badge variant="destructive">
                            user crop feedback
                          </Badge>
                        ) : null}
                      </div>
                      {scene.subjects.some(
                        (subject) =>
                          subject.detector || subject.coordinate_space_id,
                      ) ? (
                        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                          {scene.subjects.map((subject, index) => (
                            <div
                              key={`provenance-${subject.subject_id ?? subject.label ?? index}`}
                            >
                              {subject.label ?? subject.kind ?? "subject"}:{" "}
                              {subject.detector ?? "detector not recorded"}
                              {subject.coordinate_space_id
                                ? ` · ${subject.coordinate_space_id}`
                                : ""}
                              {subject.identity_required
                                ? " · identity required; v1 has no verification result"
                                : ""}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    {scene.plannedStrategy &&
                    scene.appliedStrategy &&
                    scene.plannedStrategy !== scene.appliedStrategy ? (
                      <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                        Planned {issueLabel(scene.plannedStrategy)}, but the
                        renderer reported {issueLabel(scene.appliedStrategy)}.
                      </div>
                    ) : null}

                    <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          Intent
                        </dt>
                        <dd>
                          {[scene.intentMode, scene.intentSource]
                            .filter(Boolean)
                            .join(" · ") || "automatic / legacy"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          Two-stage frame
                        </dt>
                        <dd className="font-mono text-xs">
                          {scene.providerAspect ?? "provider n/a"} →{" "}
                          {scene.deliveryAspect ?? "delivery n/a"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          Framing latency
                        </dt>
                        <dd>
                          {scene.latencyMs == null
                            ? "n/a"
                            : formatMs(scene.latencyMs)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted-foreground">
                          Framing cost
                        </dt>
                        <dd>
                          {scene.costUsd == null
                            ? "n/a"
                            : `$${scene.costUsd.toFixed(4)}`}
                        </dd>
                      </div>
                    </dl>

                    <div className="mt-4">
                      <div className="text-xs text-muted-foreground">
                        Protected subjects
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {scene.subjects.length > 0 ? (
                          scene.subjects.map((subject) => (
                            <Badge
                              key={
                                subject.subject_id ??
                                subject.label ??
                                `${subject.kind}:unknown`
                              }
                              variant={
                                subject.priority === "must_keep"
                                  ? "default"
                                  : "outline"
                              }
                            >
                              {subjectLabel(subject)}
                              {subject.priority
                                ? ` · ${issueLabel(subject.priority)}`
                                : ""}
                              {subject.confidence != null
                                ? ` · ${Math.round(subject.confidence * 100)}%`
                                : ""}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            No explicit subject was recorded.
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 rounded-md bg-muted/50 p-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">
                          Reported final-pixel QC
                        </span>
                        <Badge
                          variant={
                            scene.qc?.status === "failed"
                              ? "destructive"
                              : "outline"
                          }
                          className="capitalize"
                        >
                          {scene.qc?.status ?? "not checked"}
                        </Badge>
                        {(scene.qc?.checked_coordinate_space_id ??
                        scene.qc?.checked_space_id) ? (
                          <span className="font-mono text-xs text-muted-foreground">
                            {scene.qc.checked_coordinate_space_id ??
                              scene.qc.checked_space_id}
                          </span>
                        ) : null}
                      </div>
                      {(scene.qc?.issue_codes?.length ?? 0) > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {scene.qc?.issue_codes?.map((issue) => (
                            <Badge key={issue} variant="destructive">
                              {issueLabel(issue)}
                            </Badge>
                          ))}
                        </div>
                      ) : null}
                      {metricText(scene.qc?.metrics) ? (
                        <p className="mt-2 text-xs text-muted-foreground">
                          {metricText(scene.qc?.metrics)}
                        </p>
                      ) : null}
                      {scene.fallbackReason ? (
                        <p className="mt-2 text-xs">
                          Fallback reason: {issueLabel(scene.fallbackReason)}
                        </p>
                      ) : null}
                    </div>

                    <FramingEvidenceImage
                      media={selectCorrelatedFramingDiagnostic(media, scene)}
                      sceneId={scene.sceneId}
                    />

                    <FramingSceneInspectorPanel
                      sessionId={sessionId}
                      sceneId={scene.sceneId}
                      attemptId={scene.attemptId}
                      canViewTechnical={canViewTechnical}
                    />

                    <details className="mt-4 text-sm">
                      <summary className="cursor-pointer font-medium">
                        Evidence &amp; event history ({scene.events.length})
                      </summary>
                      <div className="mt-2 space-y-2 border-l pl-3">
                        {scene.sourceRef ? (
                          <p className="break-all font-mono text-xs">
                            source: {scene.sourceRef}
                          </p>
                        ) : null}
                        {scene.events.map((event) => (
                          <div key={String(event.id)} className="text-xs">
                            <span className="font-medium">
                              {event.event_type.replaceAll(".", " › ")}
                            </span>{" "}
                            <span className="text-muted-foreground">
                              {formatDateTime(event.occurred_at)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </details>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
